import React, { lazy, Suspense, useState, useEffect } from "react";
import { Phone, Star, ArrowDown, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PROPERTY_IMAGES } from "@/lib/propertyImages";
import { useIsMobile } from "@/hooks/use-mobile";

const LeadForm = lazy(() => import("./LeadForm"));
const LazyLeadFormWrapper = () => {
  const isMobile = useIsMobile();
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    // Render the form directly on mobile (skip the Load Form button)
    if (isMobile) {
      setShouldLoad(true);
      return;
    }
    const timer = setTimeout(() => setShouldLoad(true), 1500);
    const handleInteraction = () => setShouldLoad(true);
    window.addEventListener("mousemove", handleInteraction, { once: true });
    window.addEventListener("scroll", handleInteraction, { once: true });
    window.addEventListener("touchstart", handleInteraction, { once: true });
    window.addEventListener("force-load-form", handleInteraction);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("mousemove", handleInteraction);
      window.removeEventListener("scroll", handleInteraction);
      window.removeEventListener("touchstart", handleInteraction);
      window.removeEventListener("force-load-form", handleInteraction);
    };
  }, [isMobile]);

  if (!shouldLoad) {
    return (
      <div className="h-full w-full flex flex-col items-center justify-center gap-4 py-12">
        <h3 className="text-xl font-bold text-center">Start Income Analysis</h3>
        <Button
          onClick={() => setShouldLoad(true)}
          className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold px-8 py-6 rounded-full text-lg"
        >
          Load Form
        </Button>
      </div>
    );
  }
  return <LeadForm />;
};

interface HeroProps {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
}

const Hero = ({ title, subtitle }: HeroProps) => {
  return (
    <section className="relative min-h-[92vh] flex items-center overflow-hidden">
      {/* Cinematic full-bleed property background */}
      <div className="absolute inset-0 z-0">
        <img
          src={PROPERTY_IMAGES.cozyLivingRoom}
          alt="Cozy, beautifully staged living room in a Northern Virginia Airbnb managed by JNN Homes"
          className="w-full h-full object-cover"
          fetchPriority="high"
        />
        {/* Cinematic darkening for legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/55 to-background/95" />
        <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/50 to-background/70" />
        <div className="absolute inset-0 bg-background/30" />
      </div>

      <div className="container relative z-10 mx-auto px-4 w-full pt-12 pb-20">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Left — Cinematic editorial statement */}
          <div className="lg:col-span-7 max-w-2xl space-y-7">
            <div className="reveal is-visible">
              <span className="inline-flex items-center gap-3 bg-white/90 backdrop-blur-sm rounded-full pl-4 pr-5 py-2 shadow-sm border border-border/40">
                <span className="h-px w-8 bg-primary" />
                <span className="text-xs font-semibold uppercase tracking-[0.35em] text-primary">
                  DC Metro Airbnb Management
                </span>
              </span>
            </div>

            <h1 className="reveal is-visible reveal-delay-1 text-4xl md:text-6xl lg:text-7xl font-heading font-bold md:font-medium leading-[1.02] text-foreground tracking-tight">
              {title || (
                <>
                  <span className="text-gradient font-bold md:font-semibold">
                    Maximize Your Airbnb Income.
                  </span>{" "}
                  We Handle Everything.
                </>
              )}
            </h1>

            <p className="reveal is-visible reveal-delay-2 text-lg md:text-2xl text-foreground/80 leading-relaxed max-w-xl font-light">
              {subtitle ||
                "Full-service short-term rental management for property owners across VA & DC. Dynamic pricing, professional cleaning, guest communication, and maintenance — all handled by an active STR investor and Airbnb Superhost."}
            </p>

            <div className="reveal is-visible reveal-delay-3 flex flex-wrap items-center gap-x-7 gap-y-3">
              <Button
                asChild
                size="lg"
                className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-8 py-7 rounded-full text-base glow-primary"
              >
                <a href="#lead-form" className="flex items-center gap-2">
                  Get a Free Rental Analysis
                  <ArrowRight className="w-5 h-5" />
                </a>
              </Button>
              <a
                href="tel:7038905425"
                className="flex items-center gap-2 text-foreground font-semibold hover:text-primary transition-colors text-base link-underline"
              >
                <Phone className="w-4 h-4 text-primary" />
                (703) 890-5425
              </a>
            </div>
          </div>

          {/* Right — Lead form card */}
          <div
            id="lead-form"
            className="reveal is-visible reveal-delay-2 lg:col-span-5 glass border border-border/60 p-6 md:p-8 rounded-2xl shadow-2xl shadow-black/20 min-h-[440px] glow-primary"
          >
            <Suspense
              fallback={
                <div className="h-full w-full flex items-center justify-center text-muted-foreground">
                  Loading form...
                </div>
              }
            >
              <LazyLeadFormWrapper />
            </Suspense>
          </div>
        </div>

        <p className="mt-4 lg:mt-6 text-center text-[0.7rem] sm:text-xs text-foreground/70 leading-relaxed max-w-md mx-auto">
          Offer for new Washington DC and Manassas owner clients only, limited
          to the first 10 homes signed. After 3 months, standard plan pricing
          (15%, 20% or 25%) applies.
        </p>
      </div>

      {/* Subtle scroll cue */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1.5 text-foreground/50">
        <span className="text-[0.65rem] font-medium uppercase tracking-[0.3em]">
          Scroll
        </span>
        <ArrowDown className="w-4 h-4 animate-bounce" />
      </div>
    </section>
  );
};

export default Hero;
